import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Star, UserCheck, Database, CreditCard, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Category {
  categoryName: string;
  points: string[];
}

interface SubscriptionPlan {
  id: number;
  subscriptionName: string;
  shortName: string;
  country: string;
  currency: string;
  utilityServices: number | null;
  description: string | null;
  permission: string | null;
  fairUsage: string | null;
  validity: string;
  status: number | null;
  isActive: boolean;
}

interface SubscriptionDetails {
  id: number;
  subscriptionPlan: SubscriptionPlan;
  country: string;
  currency: string;
  baseRate: number;
  paymentFrequency: string;
  isTaxable: boolean;
  taxRate: number;
  effectiveDate: string;
  isActive: boolean;
  currentPlan: boolean;
  subscriptionId: number;
  subscriptionEndDate: string;
  isPreviousPlan: boolean;
  categories: Category[];
}

interface TrialPlanCardProps {
  subscriptionDetails: SubscriptionDetails[];
}

export function TrialPlanCard({ subscriptionDetails }: TrialPlanCardProps) {
  const navigate = useNavigate();
  const currentPlan = subscriptionDetails.find((plan) => plan.currentPlan);
  const isAnyPlanFree = subscriptionDetails.some((plan) => plan.baseRate === 0);

  const section0Features =
    currentPlan?.categories.find((cat) => cat.categoryName === "Section_0")
      ?.points ?? [];

  const ICON_MAP: Record<string, React.ReactNode> = {
    CX: <UserCheck className="w-4 h-4 text-orange-500 mr-1" />,
    MX: <Database className="w-4 h-4 text-blue-600 mr-1" />,
    BX: <CreditCard className="w-4 h-4 text-purple-500 mr-1" />,
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center pb-2">
        <CardTitle className="text-md font-medium flex items-center">
          <Star className="w-4 h-4 mr-2 text-yellow-500" />
          {currentPlan?.subscriptionPlan.subscriptionName ?? "Trial Plan"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="text-sm text-muted-foreground">
              Your current subscription plan
            </div>
            <div className="text-3xl font-bold mt-1">
              {isAnyPlanFree ? "Free" : "Paid"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Up to 5 users included
            </div>
          </div>

          <div className="space-y-2">
            {section0Features.map((point, idx) => {
              const key = point.match(/\((\w+)\)/)?.[1] || "GEN";
              return (
                <div key={idx} className="flex items-center space-x-2 text-sm">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="flex items-center rounded px-2 py-1 text-xs font-medium  text-gray-700">
                    {ICON_MAP[key] ?? null}
                    {point}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="text-xs text-muted-foreground mt-2">
            All the essentials your org needs.
          </div>

          <div className="mt-3">
            <Button
  variant="outline"
  className="w-full cursor-not-allowed opacity-50 hover:bg-transparent hover:text-gray-500"
  disabled
>
  Update Subscription
</Button>

          </div>
        </div>
      </CardContent>
    </Card>
  );
}
